import { getDomain } from 'tldts';

type ExternalLinkProps = {
  href: string;
  rel?: 'noopener noreferrer';
  target?: '_blank';
};

export const NEW_TAB_PROPS = {
  rel: 'noopener noreferrer',
  target: '_blank',
} as const satisfies Omit<ExternalLinkProps, 'href'>;

const SAME_SITE_PROTOCOLS = ['http:', 'https:'];

const parseUrl = (url: string, base?: string) => {
  try {
    return new URL(url, base);
  } catch {
    return null;
  }
};

const registrableDomain = (hostname: string) =>
  // allowPrivateDomains keeps sibling deploys on shared hosts apart: without it
  // `a.vercel.app` and `b.vercel.app` would both reduce to `vercel.app`.
  getDomain(hostname, { allowPrivateDomains: true });

const isSameSite = (destinationHostname: string, currentHostname: string) => {
  if (destinationHostname === currentHostname) return true;

  const destinationDomain = registrableDomain(destinationHostname);
  const currentDomain = registrableDomain(currentHostname);

  // No registrable domain means no subdomain to ignore (IP addresses,
  // `localhost`, internal-only hostnames), so exact match above was the answer.
  if (!destinationDomain || !currentDomain) return false;

  return destinationDomain === currentDomain;
};

/**
 * Anchor props for linking to `destination` from a page served at `comparisonUrl`.
 *
 * Subdomains do not make a link external: a Bloom menu proxied onto
 * `shop.dispensary.com` treats `www.dispensary.com` as the same site, but
 * `iheartjane.com` as external. Comparison is by registrable domain (public
 * suffix list) rather than by label count, so `foo.co.uk` and `bar.co.uk` stay
 * distinct.
 *
 * Anything that does not resolve to an http(s) URL — `mailto:`, `tel:`, a
 * malformed `comparisonUrl` — is treated as external, so it never loses
 * `rel="noopener"`.
 */
export const externalLinkProps = (
  destination: string,
  comparisonUrl: string
): ExternalLinkProps => {
  const currentUrl = parseUrl(comparisonUrl);
  const destinationUrl = parseUrl(destination, currentUrl?.href);

  if (!currentUrl || !destinationUrl) {
    return { href: destination, ...NEW_TAB_PROPS };
  }

  if (!SAME_SITE_PROTOCOLS.includes(destinationUrl.protocol)) {
    return { href: destination, ...NEW_TAB_PROPS };
  }

  if (!isSameSite(destinationUrl.hostname, currentUrl.hostname)) {
    return { href: destination, ...NEW_TAB_PROPS };
  }

  return { href: destination };
};
